import { useEffect, useId, useRef, useState } from "react";
import { send, upload, type PracticeAttachment } from "./api.js";
import { formatDateTime } from "./format.js";
import "./practiceAttachments.css";

/**
 * What a family attached to a practice session, and how to add more.
 *
 * The same component on the staff student page, without the adding — seeing
 * the photo of the page somebody got stuck on is most of what this is worth to
 * a tutor about to teach them.
 *
 * A YouTube video is a still and a play button until somebody asks for it. A
 * page of thirty sessions could otherwise be thirty embedded players, each of
 * them loading somebody else's scripts on a child's phone.
 */

const ACCEPT_WITH_RECORDINGS = "image/*,audio/*,video/*,application/pdf";
const ACCEPT_WITHOUT_RECORDINGS = "image/*,video/*,application/pdf";

export function PracticeAttachments({
  base,
  attachments,
  canEdit = false,
  taskOptions = [],
  recordingsEnabled = false,
  onChanged,
}: {
  /** `…/practice/:sessionId/attachments`, on whichever side is asking. */
  base: string;
  attachments: PracticeAttachment[];
  canEdit?: boolean;
  taskOptions?: Array<{ id: string; label: string }>;
  recordingsEnabled?: boolean;
  onChanged?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [addingLink, setAddingLink] = useState(false);
  const [url, setUrl] = useState("");
  const [practiceTodoId, setPracticeTodoId] = useState(taskOptions.length === 1 ? taskOptions[0]!.id : "");
  const [reflection, setReflection] = useState("");
  const urlInputId = useId();
  const taskInputId = useId();
  const reflectionInputId = useId();
  const [playing, setPlaying] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const uploadKey = useRef<{ signature: string; key: string } | null>(null);

  useEffect(() => {
    if (!practiceTodoId && taskOptions.length === 1) setPracticeTodoId(taskOptions[0]!.id);
  }, [practiceTodoId, taskOptions]);

  async function act(what: () => Promise<unknown>, failure: string) {
    setBusy(true);
    setProblem(null);
    try {
      await what();
      onChanged?.();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : failure);
    } finally {
      setBusy(false);
    }
  }

  if (attachments.length === 0 && !canEdit) return null;

  return (
    <div className="attachments">
      {problem && <div className="error small" role="alert">{problem}</div>}

      {attachments.map((attachment) => {
        const src = `/api${base}/${attachment.id}`;
        const type = attachment.fileType ?? "";

        return (
          <figure className="attachment" key={attachment.id}>
            {attachment.kind === "youtube" ? (
              playing === attachment.id ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${attachment.videoId}`}
                  title="YouTube video"
                  allow="accelerometer; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button type="button" className="quiet" onClick={() => setPlaying(attachment.id)}>
                  ▶ Play the video
                </button>
              )
            ) : type.startsWith("image/") ? (
              <a href={src} target="_blank" rel="noreferrer">
                <img src={src} alt={attachment.fileName} loading="lazy" />
              </a>
            ) : type.startsWith("audio/") ? (
              <audio
                className="attachment-audio"
                src={src}
                controls
                preload="metadata"
                aria-label={`Listen to ${attachment.fileName}`}
              />
            ) : type.startsWith("video/") ? (
              <video src={src} controls preload="metadata" aria-label={`Watch ${attachment.fileName}`} />
            ) : (
              <a href={src} target="_blank" rel="noreferrer" className="small">
                {type === "application/pdf" ? "📄" : "📎"} {attachment.fileName}
              </a>
            )}

            <figcaption className="small muted">
              {attachment.kind === "youtube" ? "YouTube" : attachment.fileName}
              {attachment.uploadedAt && <> · Added {formatDateTime(attachment.uploadedAt)}</>}
              {attachment.practiceTaskTitle && <> · For {attachment.practiceTaskTitle}</>}
              {canEdit && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() =>
                      void act(() => send("DELETE", `${base}/${attachment.id}`), "Could not remove that.")
                    }
                  >
                    remove
                  </button>
                </>
              )}
            </figcaption>
            {attachment.reflection && (
              <p className="small muted">Reflection: {attachment.reflection}</p>
            )}
          </figure>
        );
      })}

      {canEdit && (
        <div className="row small" style={{ flexWrap: "wrap" }}>
          <input
            ref={picker}
            type="file"
            accept={recordingsEnabled && taskOptions.length > 0 ? ACCEPT_WITH_RECORDINGS : ACCEPT_WITHOUT_RECORDINGS}
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared straight away, so choosing the same file twice after a
              // failure still counts as a change.
              event.target.value = "";
              if (file) void act(async () => {
                const fileType = file.type.toLowerCase();
                const isAudioRecording = fileType.startsWith("audio/");
                const selectedTask = practiceTodoId || (taskOptions.length === 1 ? taskOptions[0]!.id : "");
                const isVideoSubmission = fileType.startsWith("video/") && recordingsEnabled && Boolean(selectedTask);
                const isRecording = isAudioRecording || isVideoSubmission;
                if (isAudioRecording && !recordingsEnabled) {
                  throw new Error("Practice recording uploads are not enabled.");
                }
                if (isAudioRecording && !selectedTask) {
                  throw new Error("Choose the practice task this recording belongs to.");
                }
                const signature = `${file.name}:${file.size}:${file.lastModified}`;
                if (!uploadKey.current || uploadKey.current.signature !== signature) {
                  uploadKey.current = { signature, key: crypto.randomUUID() };
                }
                await upload(base, file, {
                  ...(selectedTask ? { practiceTodoId: selectedTask } : {}),
                  ...(recordingsEnabled && reflection.trim() ? { reflection: reflection.trim() } : {}),
                  ...(isRecording ? { submissionKind: "practice_recording" } : {}),
                  uploadRequestKey: uploadKey.current.key,
                });
                uploadKey.current = null;
                setReflection("");
              }, "Could not attach that.");
            }}
          />
          <button type="button" className="quiet small" disabled={busy} onClick={() => picker.current?.click()}>
            {busy ? "Working…" : recordingsEnabled && taskOptions.length > 0 ? "Add a photo, recording or PDF" : "Add a photo, video clip or PDF"}
          </button>

          {recordingsEnabled && (
            <>
              {taskOptions.length > 0 ? (
                <>
                  <label htmlFor={taskInputId} className="small">Link to practice task</label>
                  <select
                    id={taskInputId}
                    value={practiceTodoId}
                    onChange={(event) => setPracticeTodoId(event.target.value)}
                    disabled={busy}
                  >
                    <option value="">No task link</option>
                    {taskOptions.map((task) => <option key={task.id} value={task.id}>{task.label}</option>)}
                  </select>
                  <label htmlFor={reflectionInputId} className="small">Reflection (optional)</label>
                  <input
                    id={reflectionInputId}
                    type="text"
                    maxLength={500}
                    value={reflection}
                    onChange={(event) => setReflection(event.target.value)}
                    placeholder="What would you like your tutor to notice?"
                    disabled={busy}
                  />
                </>
              ) : (
                <p className="small muted">Track practice against an assigned task before uploading a recording.</p>
              )}
            </>
          )}

          {addingLink ? (
            <>
              <label htmlFor={urlInputId} className="small">YouTube URL</label>
              <input
                id={urlInputId}
                type="url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="Paste a YouTube link"
                style={{ flex: 1, minWidth: 180 }}
              />
              <button
                type="button"
                className="small"
                disabled={busy || url.trim() === ""}
                onClick={() =>
                  void act(async () => {
                    await send("POST", `${base}/links`, { url });
                    setUrl("");
                    setAddingLink(false);
                  }, "Could not add that link.")
                }
              >
                Add
              </button>
              <button type="button" className="quiet small" onClick={() => setAddingLink(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="quiet small" onClick={() => setAddingLink(true)}>
              Add a YouTube link
            </button>
          )}
        </div>
      )}
    </div>
  );
}
